///
/// EarthBrowser
///
/// Created by Matt Giger on September 13, 2026
/// Copyright (c) 2026 Geodata Labs
///

/// Storage Manager: inspect and manage all browser-side storage (OPFS/VFS
/// volumes, GeoPackages, IndexedDB, localStorage) with a factory-reset
/// last resort for customer support.

import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { logger } from '@/utils/LogDispatcher'
import { Download, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { scanStorage, type StorageSnapshot } from '@/components/features/dev-tools/storage-manager/storageScanner'
import { exportSnapshot, VFSLockedError } from '@/components/features/dev-tools/storage-manager/storageActions'
import { OverviewTab } from '@/components/features/dev-tools/storage-manager/OverviewTab'
import { VolumesTab } from '@/components/features/dev-tools/storage-manager/VolumesTab'
import { IndexedDBTab } from '@/components/features/dev-tools/storage-manager/IndexedDBTab'
import { LocalStorageTab } from '@/components/features/dev-tools/storage-manager/LocalStorageTab'

export interface StorageTabProps {
	snapshot: StorageSnapshot
	busy: boolean
	runAction: (action: () => Promise<void>) => Promise<void>
}

export function StorageManager() {
	const [snapshot, setSnapshot] = useState<StorageSnapshot | null>(null)
	const [busy, setBusy] = useState(false)

	const refresh = useCallback(async () => {
		setBusy(true)
		try {
			setSnapshot(await scanStorage())
		} catch (error) {
			logger.error('Failed to scan storage:', [[error]])
		} finally {
			setBusy(false)
		}
	}, [])

	/// Run a destructive action, surface errors, and rescan afterwards
	const runAction = useCallback(async (action: () => Promise<void>) => {
		setBusy(true)
		try {
			await action()
		} catch (error) {
			logger.error('Storage action failed:', [[error]])
			if (error instanceof VFSLockedError) {
				alert(`${error.message}`)
			} else {
				alert(`Operation failed: ${error instanceof Error ? error.message : String(error)}`)
			}
		} finally {
			setBusy(false)
		}
		await refresh()
	}, [refresh])

	useEffect(() => {
		void refresh()
	}, [refresh])

	if (!snapshot) {
		return (
			<div className="flex items-center justify-center h-full text-sm text-muted-foreground gap-2 p-8">
				<RefreshCw className="h-4 w-4 animate-spin" />
				Scanning browser storage…
			</div>
		)
	}

	const tabProps: StorageTabProps = { snapshot, busy, runAction }

	return (
		<div className="p-4 space-y-4">
			<div className="flex items-center justify-between gap-2">
				<div className="text-sm text-muted-foreground">
					Inspect and manage everything EarthBrowser stores in this browser
				</div>
				<div className="flex gap-2">
					<Button variant="outline" size="sm" onClick={() => exportSnapshot(snapshot)} disabled={busy}>
						<Download className="h-4 w-4 mr-2" />
						Export
					</Button>
					<Button variant="outline" size="sm" onClick={refresh} disabled={busy}>
						<RefreshCw className={`h-4 w-4 mr-2 ${busy ? 'animate-spin' : ''}`} />
						Refresh
					</Button>
				</div>
			</div>

			<Tabs defaultValue="overview">
				<TabsList className="w-full justify-start">
					<TabsTrigger value="overview">Overview</TabsTrigger>
					<TabsTrigger value="volumes">
						Database Volumes ({snapshot.volumes.length})
					</TabsTrigger>
					<TabsTrigger value="indexeddb">
						IndexedDB ({snapshot.indexedDBs.length})
					</TabsTrigger>
					<TabsTrigger value="localstorage">
						Local Storage ({snapshot.localStorageItems.length})
					</TabsTrigger>
				</TabsList>
				<TabsContent value="overview">
					<OverviewTab {...tabProps} />
				</TabsContent>
				<TabsContent value="volumes">
					<VolumesTab {...tabProps} />
				</TabsContent>
				<TabsContent value="indexeddb">
					<IndexedDBTab {...tabProps} />
				</TabsContent>
				<TabsContent value="localstorage">
					<LocalStorageTab {...tabProps} />
				</TabsContent>
			</Tabs>
		</div>
	)
}
